import React, { useState } from 'react';
import { Upload, X, AlertTriangle, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';

// ImportProjectModal: pick a project-bundle JSON (produced by the
// "Export Project" button on a project detail page) -> client-side summary ->
// POST /api/projects/projects/import-bundle. The backend creates a brand-new
// project (fresh IDs throughout). User references travel as emails and
// resolve softly — unknown emails import unassigned and come back in
// `warnings` instead of failing the import, so dev→local moves work even
// when the people don't exist locally.

const BUNDLE_FORMAT = 'boss.project_bundle';

const TYPE_LABEL = {
  waterfall: 'Waterfall',
  agile: 'Agile',
  waves_threads: 'Waves & Threads',
};

export default function ImportProjectModal({ isOpen, onClose, onImported }) {
  const { hasPermission } = usePermissions();
  const canImport = hasPermission('projects.project.create');

  const [bundle, setBundle] = useState(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setBundle(null);
    setFileName('');
    setParseError('');
    setName('');
    setResult(null);
    setSubmitting(false);
  };

  const close = () => {
    if (!submitting) {
      reset();
      onClose?.();
    }
  };

  const finish = () => {
    // Capture before reset — reset clears the result.
    const project = result?.project;
    reset();
    onClose?.();
    if (project) onImported?.(project);
  };

  const handleFileChange = async (e) => {
    setResult(null);
    setParseError('');
    setBundle(null);
    const f = e.target.files?.[0];
    if (!f) return;
    // Re-arm the input: browsers only fire `change` when the value differs,
    // so a same-named re-pick after a parse failure would otherwise be
    // silently ignored. We already hold the File reference in `f`.
    e.target.value = '';
    setFileName(f.name);
    // Guard before f.text() — a runaway file would otherwise be read fully
    // into memory before any format check fires. Real bundles are well under
    // a megabyte per ~1000 tasks; 20 MB is generous.
    if (f.size > 20 * 1024 * 1024) {
      setParseError('File too large (max 20 MB).');
      return;
    }
    try {
      const text = await f.text();
      const parsed = JSON.parse(text);
      if (parsed?.format !== BUNDLE_FORMAT || !parsed?.project?.name) {
        setParseError(
          'Not a BOSS project bundle. Use "Export Project" on a project detail page to produce one.'
        );
        return;
      }
      setBundle(parsed);
      // Default import name is "<source name> (imported)"; the backend
      // dedupes repeat imports to "(2)", "(3)", ... server-side, where the
      // full (paginated-away) project list is visible.
      setName(`${parsed.project.name} (imported)`.slice(0, 200));
    } catch {
      setParseError('File could not be parsed as JSON.');
    }
  };

  const handleSubmit = async () => {
    if (!bundle || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post(`${API_URL}/api/projects/projects/import-bundle`, {
        bundle,
        name: name.trim() || undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data?.detail;
        const msg = Array.isArray(detail)
          ? detail
              .map((d) => (typeof d === 'string' ? d : d?.error ?? d?.msg ?? ''))
              .filter(Boolean)
              .slice(0, 5)
              .join('; ')
          : typeof detail === 'string'
            ? detail
            : 'Import failed.';
        toast.error(msg || 'Import failed.');
        return;
      }
      toast.success(
        `Imported project with ${data.task_count} task${data.task_count === 1 ? '' : 's'}`
      );
      if (data.warnings?.length) {
        // Stay open so the user reads what was dropped/unassigned;
        // they continue via the "Open project" footer button.
        setResult(data);
      } else {
        reset();
        onClose?.();
        onImported?.(data.project);
      }
    } catch {
      toast.error('Import failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !canImport) return null;

  const counts = bundle
    ? {
        tasks: bundle.tasks?.length ?? 0,
        sprints: bundle.sprints?.length ?? 0,
        waves: bundle.waves?.length ?? 0,
        threads: bundle.threads?.length ?? 0,
      }
    : null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-project-modal-title"
      data-testid="import-project-modal"
    >
      <div className="absolute inset-0" onClick={close} aria-hidden="true" />

      <div className="relative bg-boss-surface border border-boss-border rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3 border-b border-boss-border shrink-0">
          <div>
            <h3
              id="import-project-modal-title"
              className="text-base font-semibold text-boss-text"
            >
              Import project
            </h3>
            <p className="text-xs text-boss-text-muted mt-0.5">
              Re-create a project from a bundle exported with &quot;Export Project&quot;.
              A new project is created — nothing existing is overwritten.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="text-boss-text-muted hover:text-boss-text-secondary transition shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 pt-3 pb-5 space-y-6">
          {!bundle ? (
            <div className="border-2 border-dashed border-boss-border rounded-lg p-8 text-center">
              <Upload className="mx-auto text-boss-text-muted mb-2" size={28} />
              <p className="text-sm text-boss-text-secondary mb-3">
                Pick a <span className="font-mono text-xs">.json</span> project bundle.
              </p>
              <label className="btn-secondary inline-flex items-center gap-1.5 text-xs cursor-pointer">
                <Upload size={12} />
                Choose bundle
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="import-project-file-input"
                />
              </label>
              {parseError ? (
                <p className="text-xs text-red-400 mt-3" data-testid="import-project-parse-error">
                  {parseError}
                </p>
              ) : null}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between bg-boss-elevated/40 rounded-lg px-3 py-2 -mt-1">
                <span className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="shrink-0 text-[var(--boss-info)]" />
                  <span className="text-sm font-medium text-[var(--boss-info)] truncate">{fileName}</span>
                </span>
                <button
                  type="button"
                  onClick={reset}
                  disabled={submitting}
                  className="text-xs text-boss-text-muted hover:text-boss-text disabled:opacity-50"
                  data-testid="import-project-clear"
                >
                  Clear
                </button>
              </div>

              <div className="border-t border-boss-border" aria-hidden="true" />

              <div className="space-y-2 px-3 !mt-2" data-testid="import-project-summary">
                <p className="text-[11px] uppercase tracking-wider text-boss-text-secondary">
                  Bundle contents
                </p>
                <div className="text-sm text-boss-text-secondary">
                  <span className="font-semibold text-boss-text">
                    {TYPE_LABEL[bundle.project?.project_type] ?? bundle.project?.project_type}
                  </span>
                  <span className="mx-1.5">·</span>
                  {counts.tasks} task{counts.tasks === 1 ? '' : 's'}
                  {counts.sprints > 0 && <>, {counts.sprints} sprint{counts.sprints === 1 ? '' : 's'}</>}
                  {counts.waves > 0 && <>, {counts.waves} wave{counts.waves === 1 ? '' : 's'}</>}
                  {counts.threads > 0 && <>, {counts.threads} thread{counts.threads === 1 ? '' : 's'}</>}
                </div>
              </div>

              <div>
                <label
                  htmlFor="import-project-name"
                  className="block text-[11px] uppercase tracking-wider text-boss-text-secondary mb-0.5 px-3"
                >
                  Project name
                </label>
                <input
                  id="import-project-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submitting || !!result}
                  maxLength={200}
                  className="boss-input w-full h-10 px-3 text-sm"
                  data-testid="import-project-name-input"
                />
              </div>

              {result ? (
                <div className="space-y-2" data-testid="import-project-result">
                  <div className="flex items-start gap-2 rounded-md bg-green-500/10 px-3 py-2 text-sm text-green-400">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                    <span>
                      Created project with {result.task_count} task{result.task_count === 1 ? '' : 's'}.
                    </span>
                  </div>
                  {(result.warnings?.length ?? 0) > 0 ? (
                    <div className="rounded-md bg-yellow-500/10 px-3 py-2">
                      <div className="flex items-center gap-2 text-sm text-yellow-400 font-medium mb-1.5">
                        <AlertTriangle size={14} />
                        {result.warnings.length} warning{result.warnings.length === 1 ? '' : 's'}
                      </div>
                      <ul className="space-y-0.5 text-xs text-yellow-200/90 max-h-[160px] overflow-y-auto pr-1">
                        {result.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-5 pt-3 border-t border-boss-border shrink-0">
          {!result ? (
            <>
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="btn-secondary text-xs disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!bundle || submitting || !name.trim()}
                className="btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                data-testid="import-project-submit"
              >
                {submitting ? 'Importing…' : 'Import Project'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={finish}
              className="btn-primary text-xs"
              data-testid="import-project-open"
            >
              Open project
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
